import { motion } from "framer-motion";
import { User, Package, Shield, Settings, MoreHorizontal } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface MobileSettingsNavigationProps {
  orderCount?: number;
}

const navItems = [
  { id: "profile", label: "Profile", icon: User, path: "/settings" },
  { id: "orders", label: "Orders", icon: Package, path: "/settings?tab=orders" },
  { id: "security", label: "Security", icon: Shield, path: "/settings?tab=security" },
  { id: "preferences", label: "Settings", icon: Settings, path: "/settings?tab=appearance" },
  { id: "more", label: "More", icon: MoreHorizontal, path: "/settings?tab=profile" },
];

export function MobileSettingsNavigation({ orderCount = 0 }: MobileSettingsNavigationProps) {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "profile";

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t pb-safe"
    >
      <nav className="grid grid-cols-5 h-16" role="navigation" aria-label="Mobile settings navigation">
        {navItems.map((item) => {
          const isActive = 
            (item.id === "profile" && !searchParams.get("tab")) ||
            (item.id === "orders" && activeTab === "orders") ||
            (item.id === "security" && activeTab === "security") ||
            (item.id === "preferences" && (activeTab === "appearance" || activeTab === "notifications")) ||
            (item.id === "more" && !["orders", "security", "appearance", "notifications"].includes(activeTab));
          
          const Icon = item.icon;

          return (
            <Link
              key={item.id}
              to={item.path}
              className={cn(
                "relative flex flex-col items-center justify-center gap-1 transition-colors min-h-[56px]",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
              aria-current={isActive ? "page" : undefined}
            >
              <motion.div
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <Icon className="h-5 w-5" />
                {item.id === "orders" && orderCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-[10px] animate-pulse"
                  >
                    {orderCount > 99 ? "99+" : orderCount}
                  </Badge>
                )}
              </motion.div>
              <span className={cn(
                "text-[10px] font-medium",
                isActive && "font-semibold"
              )}>
                {item.label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-primary rounded-full"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </Link>
          );
        })}
      </nav>
    </motion.div>
  );
}
